import { sb } from './db.js';
import { viewing, activeUser } from './auth.js';
import { localISO } from './phase.js';

// Active program's days for a user (default: the user being viewed).
export async function loadProgramDays(user = viewing()) {
  const { data: prog } = await sb.from('grit_programs')
    .select('id').eq('user_id', user).eq('active', true).limit(1).maybeSingle();
  if (!prog) return [];
  const { data } = await sb.from('grit_program_days')
    .select('*').eq('program_id', prog.id).order('sort');
  return data || [];
}

// A day's exercises joined to the catalog, with the effective knee flag resolved.
export async function updateProgramDay(dayId, fields) {
  return sb.from('grit_program_days').update(fields).eq('id', dayId);
}

export async function loadDayExercises(dayId) {
  const { data } = await sb.from('grit_program_exercises')
    .select('*, exercise:grit_exercises(*)').eq('program_day_id', dayId).order('sort');
  return (data || []).map((pe) => ({
    ...pe,
    knee: pe.knee_sensitive_override ?? pe.exercise?.knee_sensitive ?? false,
    tracking: pe.exercise?.tracking || 'weight',
  }));
}

// Most-recent session's sets for one exercise (prefill + "last time"). With a dayId,
// prefer the last time it was done on THAT workout day: the book tracks the same lift
// in two workouts a week separately (p.328). Falls back to any day.
export async function loadLastSets(exerciseId, user = viewing(), dayId = null) {
  let q = sb.from('grit_sessions').select('id').eq('user_id', user);
  if (dayId) q = q.eq('program_day_id', dayId);
  const { data: sess } = await q.order('date', { ascending: false }).order('created_at', { ascending: false }).limit(30);
  if (!sess?.length) return dayId ? loadLastSets(exerciseId, user, null) : [];
  const ids = sess.map((s) => s.id);
  const { data } = await sb.from('grit_sets')
    .select('*').in('session_id', ids).eq('exercise_id', exerciseId)
    .order('completed_at', { ascending: false });
  if (!data?.length) return dayId ? loadLastSets(exerciseId, user, null) : [];
  const latest = data[0].session_id;
  return data.filter((s) => s.session_id === latest);
}

export async function startSession(dayId, dateISO, madeUpForDate = null) {
  const row = { user_id: activeUser(), program_day_id: dayId, date: dateISO, status: 'partial' };
  if (madeUpForDate && madeUpForDate !== dateISO) row.made_up_for_date = madeUpForDate;
  const { data } = await sb.from('grit_sessions').insert(row).select('id').single();
  return data?.id;
}

export async function saveSet(sessionId, set) {
  const { data, error } = await sb.from('grit_sets').insert({
    session_id: sessionId,
    exercise_id: set.exerciseId,
    set_number: set.setNumber,
    weight_lb: set.weight,
    reps: set.reps,
    rir: Number.isFinite(set.rir) ? set.rir : null,
    is_warmup: !!set.isWarmup,
    swapped_from_exercise_id: set.swappedFrom || null,
    completed_at: new Date().toISOString(),
  }).select('id').single();
  if (error) { console.warn('saveSet failed', error); return null; }
  return data?.id;
}

// Update an already-saved set in place (non-destructive edit). Only the fields
// passed are written, so a weight-only fix doesn't disturb reps/warm-up.
export async function updateSet(setId, fields) {
  if (!setId) return null;
  const patch = {};
  if ('weight' in fields) patch.weight_lb = fields.weight;
  if ('reps' in fields) patch.reps = fields.reps;
  if ('isWarmup' in fields) patch.is_warmup = !!fields.isWarmup;
  if ('rir' in fields) patch.rir = Number.isFinite(fields.rir) ? fields.rir : null;
  if (!Object.keys(patch).length) return null;
  const { error } = await sb.from('grit_sets').update(patch).eq('id', setId);
  if (error) { console.warn('updateSet failed', error); return null; }
  return true;
}

export async function deleteSet(setId) {
  if (!setId) return;
  return sb.from('grit_sets').delete().eq('id', setId);
}

export async function deleteSession(sessionId) {
  if (!sessionId) return;
  return sb.from('grit_sessions').delete().eq('id', sessionId);
}

// Move a logged workout to the day it really happened (Kenny, 2026-10-22).
export async function moveSession(sessionId, dateISO) {
  const { error } = await sb.from('grit_sessions').update({ date: dateISO }).eq('id', sessionId);
  if (error) console.warn('moveSession', error);
  return !error;
}

export async function finishSession(sessionId, durationMin, notes = null) {
  const patch = { status: 'completed', duration_min: durationMin };
  if (notes && notes.trim()) patch.notes = notes.trim();
  const { error } = await sb.from('grit_sessions').update(patch).eq('id', sessionId);
  if (error) { console.warn('finishSession failed', error); return false; }
  return true;
}

// ── Running ────────────────────────────────────────────────────────────────
export async function loadGoals(user = viewing()) {
  const { data } = await sb.from('grit_goals').select('*').eq('user_id', user);
  return data || [];
}

export async function loadRunPlan(user = viewing()) {
  const { data } = await sb.from('grit_run_plans').select('*').eq('user_id', user).limit(1).maybeSingle();
  return data || null;
}

export async function loadRuns(user = viewing()) {
  const { data } = await sb.from('grit_runs').select('*').eq('user_id', user).order('date', { ascending: false });
  return data || [];
}

// Returns true when the run saved (or is safely waiting on the phone to send), false otherwise.
export async function saveRun(run) {
  const { error, pending } = await sb.from('grit_runs').insert({
    user_id: activeUser(),
    date: run.date || localISO(),
    ...run,
  });
  if (error) { console.warn('saveRun failed', error); return false; }
  return pending ? 'pending' : true;
}

export async function updateRun(runId, fields) {
  if (!runId) return false;
  const { error, pending } = await sb.from('grit_runs').update(fields).eq('id', runId);
  if (error) { console.warn('updateRun failed', error); return false; }
  return pending ? 'pending' : true;
}

// Each person's own running plan (plan v4): the one source for Today and Running.
export async function saveRunSchedule(schedule) {
  const me = activeUser();
  const { data: row } = await sb.from('grit_run_plans').select('id').eq('user_id', me).limit(1).maybeSingle();
  const { error } = row
    ? await sb.from('grit_run_plans').update({ plan: schedule }).eq('id', row.id)
    : await sb.from('grit_run_plans').insert({ user_id: me, plan: schedule });
  if (error) { console.warn('saveRunSchedule failed', error); return false; }
  return true;
}

// A race date drives the running plan (build-up when ahead, steady weeks after).
export async function setRaceDate(dateISO) {
  const { error } = await sb.from('grit_run_plans').update({ race_date: dateISO || null }).eq('user_id', activeUser());
  if (error) { console.warn('setRaceDate failed', error); return false; }
  return true;
}

// Everything one person has in Grit, as one JSON file (a backup you keep).
export async function exportAll(user = activeUser()) {
  const q = (t, col = 'user_id') => sb.from(t).select('*').eq(col, user).then((r) => r.data || []);
  const [sessions, runs, goals, food, weights, settings, notes, names, weeklyGoals, programs, plans] = await Promise.all([
    q('grit_sessions'), q('grit_runs'), q('grit_goals'), q('grit_food_log'), q('grit_body_weight'), q('grit_settings'),
    q('grit_exercise_notes'), q('grit_exercise_names'), q('grit_weekly_goals'), q('grit_programs'), q('grit_run_plans'),
  ]);
  const ids = sessions.map((x) => x.id);
  const sets = ids.length ? ((await sb.from('grit_sets').select('*, exercise:exercise_id(name)').in('session_id', ids)).data || []) : [];
  const progIds = programs.map((x) => x.id);
  const days = progIds.length ? ((await sb.from('grit_program_days').select('*').in('program_id', progIds)).data || []) : [];
  const dayIds = days.map((d) => d.id);
  const exercises = dayIds.length ? ((await sb.from('grit_program_exercises').select('*, exercise:grit_exercises(name)').in('program_day_id', dayIds)).data || []) : [];
  return { user, exported_at: new Date().toISOString(), programs, program_days: days, program_exercises: exercises, sessions, sets, runs, run_plans: plans, goals, food, weights, settings, exercise_notes: notes, exercise_names: names, weekly_goals: weeklyGoals };
}

export async function upsertGoal(goal) {
  return sb.from('grit_goals').upsert({ user_id: activeUser(), ...goal }, { onConflict: 'user_id,kind' });
}

export async function deleteRun(runId) {
  if (!runId) return;
  return sb.from('grit_runs').delete().eq('id', runId);
}

// All logged workouts for a user, newest first (for the History screen).
export async function loadSessions(user = viewing()) {
  const { data } = await sb.from('grit_sessions')
    .select('*, day:grit_program_days(name)')
    .eq('user_id', user)
    .order('date', { ascending: false })
    .order('created_at', { ascending: false });
  return data || [];
}

// Every set logged in one session, grouped by exercise in order.
export async function loadSessionSets(sessionId) {
  // grit_sets links to grit_exercises twice (exercise_id + swapped_from_exercise_id),
  // so the embed must name the FK column to stay unambiguous.
  const { data } = await sb.from('grit_sets')
    .select('*, exercise:exercise_id(name, muscle_group, tracking)')
    .eq('session_id', sessionId)
    .order('completed_at', { ascending: true });
  const groups = [];
  for (const s of data || []) {
    let g = groups.find((x) => x.exerciseId === s.exercise_id);
    if (!g) { g = { exerciseId: s.exercise_id, name: s.exercise?.name || 'Exercise', tracking: s.exercise?.tracking || 'weight', sets: [] }; groups.push(g); }
    g.sets.push(s);
  }
  return groups;
}

// All-time heaviest non-warmup work weight per exercise, for the "top weight" star.
export async function loadExerciseMaxes(user = viewing()) {
  const { data: sess } = await sb.from('grit_sessions').select('id').eq('user_id', user);
  const ids = (sess || []).map((s) => s.id);
  if (!ids.length) return {};
  const { data } = await sb.from('grit_sets').select('exercise_id, weight_lb, is_warmup').in('session_id', ids);
  const max = {};
  for (const s of data || []) {
    if (s.is_warmup || s.weight_lb == null) continue;
    const w = Number(s.weight_lb);
    if (!(s.exercise_id in max) || w > max[s.exercise_id]) max[s.exercise_id] = w;
  }
  return max;
}

// Everything the Progress tab needs from one person's workouts, in one read per table.
export async function loadProgressData(user) {
  const { data: sessions } = await sb.from('grit_sessions').select('id, date, status').eq('user_id', user);
  const ids = (sessions || []).map((s) => s.id);
  if (!ids.length) return { sessions: [], sets: [] };
  const { data: sets } = await sb.from('grit_sets').select('session_id, exercise_id, weight_lb, reps, is_warmup').in('session_id', ids);
  return { sessions: sessions || [], sets: sets || [] };
}

// ── Phone shortcut key (Settings > Phone shortcuts) ─────────────────────────
export async function shortcutKeyInfo() {
  try { const r = await fetch('/api/inbox-key', { headers: { 'x-grit-user': activeUser() } }); return await r.json(); } catch { return { ok: false }; }
}
export async function makeShortcutKey() {
  try { const r = await fetch('/api/inbox-key', { method: 'POST', headers: { 'x-grit-user': activeUser() } }); const j = await r.json(); return j.ok ? j.key : null; } catch { return null; }
}

// ── Settings ─────────────────────────────────────────────────────────────────
export async function loadSettings(user = viewing()) {
  const { data } = await sb.from('grit_settings').select('data').eq('user_id', user).maybeSingle();
  return data?.data || {};
}

export async function saveSettings(patch) {
  const cur = await loadSettings(activeUser());
  const merged = { ...cur, ...patch };
  const { error } = await sb.from('grit_settings').upsert({ user_id: activeUser(), data: merged }, { onConflict: 'user_id' });
  if (error) { console.warn('saveSettings failed', error); return null; }
  return merged;
}

// ── Program editor + creation ────────────────────────────────────────────────
export async function loadExerciseLibrary() {
  const { data } = await sb.from('grit_exercises').select('*').order('muscle_group').order('name');
  return data || [];
}

// A written-in lift. Both people share the library; the same name reuses the row, not a duplicate.
export async function createExercise(fields) {
  const name = String(fields.name || '').trim();
  const lib = await loadExerciseLibrary();
  const same = lib.find((x) => x.name.trim().toLowerCase() === name.toLowerCase());
  if (same) return same;
  const { data, error } = await sb.from('grit_exercises')
    .insert({
      name, muscle_group: fields.muscle_group || null, knee_sensitive: !!fields.knee_sensitive,
      equipment: fields.equipment || null, tracking: fields.tracking || 'weight',
      default_rep_low: fields.default_rep_low ?? 6, default_rep_high: fields.default_rep_high ?? 8,
      default_increment_lb: fields.default_increment_lb ?? 5, is_primary: (fields.default_rep_high ?? 8) <= 6,
    })
    .select('*').single();
  if (error) { console.warn('createExercise failed', error); return null; }
  return data;
}

export async function activeProgram(user = activeUser()) {
  const { data } = await sb.from('grit_programs')
    .select('*').eq('user_id', user).eq('active', true).limit(1).maybeSingle();
  return data || null;
}

export async function createProgram(name) {
  const { data, error } = await sb.from('grit_programs')
    .insert({ user_id: activeUser(), name, active: true }).select('id').single();
  if (error) { console.warn('createProgram failed', error); return null; }
  return data?.id;
}

// Apply a reviewed plan (plan v4 items 2 and 5). The new program is built switched OFF,
// then switched on in one step, so a failure part-way never leaves you without a plan.
// Old programs are only switched off: their days, and every logged workout, stay.
export async function applyPlan(preview) {
  const me = activeUser();
  const { data: prog, error: e1 } = await sb.from('grit_programs')
    .insert({ user_id: me, name: preview.name, active: false }).select('id').single();
  if (e1 || !prog) { console.warn('applyPlan: program', e1); return false; }
  for (let di = 0; di < preview.days.length; di++) {
    const d = preview.days[di];
    const day = await addProgramDay(prog.id, { name: d.name, weekday: d.weekday, kind: d.kind || 'lift', sort: di + 1 });
    if (!day) return false;
    for (let si = 0; si < d.slots.length; si++) {
      const sl = d.slots[si];
      const row = await addProgramExercise(day.id, {
        exercise_id: sl.exercise.id, sort: si, target_sets: sl.sets, rep_low: sl.lo, rep_high: sl.hi,
        rest_seconds: sl.rest, increment_lb: sl.increment, progression_mode: String(sl.required || '1'),
        start_weight_lb: sl.startWeight ?? null, carry_from_lb: sl.carryFrom ?? null,
      });
      if (!row) return false;
    }
  }
  const off = await sb.from('grit_programs').update({ active: false }).eq('user_id', me).eq('active', true);
  if (off.error) return false;
  const on = await sb.from('grit_programs').update({ active: true }).eq('id', prog.id);
  return !on.error;
}

export async function addProgramDay(programId, fields) {
  const { data, error } = await sb.from('grit_program_days')
    .insert({ program_id: programId, ...fields }).select('*').single();
  if (error) { console.warn('addProgramDay failed', error); return null; }
  return data;
}

// Returns { ok, reason }. Sessions referencing the day block deletion (NO ACTION FK).
export async function deleteProgramDay(dayId) {
  const { error } = await sb.from('grit_program_days').delete().eq('id', dayId);
  if (error) return { ok: false, reason: 'history' };
  return { ok: true };
}

export async function addProgramExercise(dayId, fields) {
  const { data, error } = await sb.from('grit_program_exercises')
    .insert({ program_day_id: dayId, ...fields }).select('*, exercise:grit_exercises(*)').single();
  if (error) { console.warn('addProgramExercise failed', error); return null; }
  return data;
}

export async function updateProgramExercise(peId, fields) {
  const { error } = await sb.from('grit_program_exercises').update(fields).eq('id', peId);
  if (error) { console.warn('updateProgramExercise failed', error); return null; }
  return true;
}

export async function deleteProgramExercise(peId) {
  if (!peId) return;
  return sb.from('grit_program_exercises').delete().eq('id', peId);
}

// Persist a new ordering by rewriting the sort column.
export async function reorderProgramExercises(orderedIds) {
  const results = await Promise.all(orderedIds.map((id, i) => sb.from('grit_program_exercises').update({ sort: i }).eq('id', id)));
  return results.every((r) => !r.error);
}

// ── Food log + body weight (nutrition) ───────────────────────────────────────
export async function loadFoodEntries(dateISO, user = viewing()) {
  const { data } = await sb.from('grit_food_log')
    .select('*').eq('user_id', user).eq('date', dateISO).order('created_at', { ascending: true });
  return data || [];
}

export async function addFoodEntry(entry) {
  const { data, error } = await sb.from('grit_food_log').insert({
    user_id: activeUser(),
    date: entry.date,
    calories: entry.calories,
    label: entry.label || null,
    protein_g: entry.protein_g ?? null,
    carbs_g: entry.carbs_g ?? null,
    fat_g: entry.fat_g ?? null,
    meal: entry.meal || null,
    source: entry.source || null,
  }).select('*').single();
  if (error) { console.warn('addFoodEntry failed', error); return null; }
  return data;
}

// Frequently-logged named foods for one-tap re-logging (auto-built from history).
export async function loadFrequentFoods(user = viewing()) {
  const { data } = await sb.from('grit_food_log')
    .select('label, calories, protein_g, carbs_g, fat_g, source, created_at')
    .eq('user_id', user).not('label', 'is', null)
    .order('created_at', { ascending: false }).limit(300);
  const byLabel = new Map();
  for (const r of data || []) {
    const key = (r.label || '').trim().toLowerCase();
    if (!key || r.source === 'mfp' || key === 'quick add') continue; // a MyFitnessPal day total is not a food to re-log
    // First occurrence wins (newest), so the macros reflect the latest version.
    if (!byLabel.has(key)) byLabel.set(key, { label: r.label, calories: r.calories, protein_g: r.protein_g, carbs_g: r.carbs_g, fat_g: r.fat_g, count: 0 });
    byLabel.get(key).count += 1;
  }
  return [...byLabel.values()].sort((a, b) => b.count - a.count).slice(0, 12);
}

export async function updateFoodEntry(id, fields) {
  const { error } = await sb.from('grit_food_log').update(fields).eq('id', id);
  if (error) { console.warn('updateFoodEntry failed', error); return null; }
  return true;
}

export async function deleteFoodEntry(id) {
  if (!id) return;
  return sb.from('grit_food_log').delete().eq('id', id);
}

// Map of YYYY-MM-DD -> total calories for the calendar / day detail.
export async function loadFoodTotalsByDate(user = viewing()) {
  const { data } = await sb.from('grit_food_log').select('date, calories').eq('user_id', user);
  const map = {};
  for (const r of data || []) map[r.date] = (map[r.date] || 0) + Number(r.calories || 0);
  return map;
}

export async function loadWeights(user = viewing()) {
  const { data } = await sb.from('grit_body_weight')
    .select('*').eq('user_id', user).order('date', { ascending: false });
  return data || [];
}

export async function saveWeight({ date, weight_lb }) {
  const { error } = await sb.from('grit_body_weight')
    .upsert({ user_id: activeUser(), date, weight_lb }, { onConflict: 'user_id,date' });
  if (error) { console.warn('saveWeight failed', error); return null; }
  return true;
}

// Every-2-weeks check-in: waist at the belly button and progress photos (book p.342-344).
export async function saveCheckin({ date, waist_in, photos_taken }) {
  const row = { user_id: activeUser(), date };
  if (waist_in !== undefined) row.waist_in = waist_in;
  if (photos_taken !== undefined) row.photos_taken = !!photos_taken;
  const { error } = await sb.from('grit_body_weight').upsert(row, { onConflict: 'user_id,date' });
  if (error) { console.warn('saveCheckin failed', error); return null; }
  return true;
}

export async function deleteWeight(id) {
  if (!id) return;
  return sb.from('grit_body_weight').delete().eq('id', id);
}

export async function latestWeight(user = viewing()) {
  const { data } = await sb.from('grit_body_weight')
    .select('weight_lb').eq('user_id', user).not('weight_lb', 'is', null).order('date', { ascending: false }).limit(1).maybeSingle();
  return data ? Number(data.weight_lb) : null;
}

// ── Lift notes: many dated notes per lift, shown every time it comes up (2026-11-03) ───
export async function loadLiftNotes(user = viewing()) {
  const { data } = await sb.from('grit_exercise_notes').select('*').eq('user_id', user)
    .order('date', { ascending: false }).order('created_at', { ascending: false });
  return data || [];
}

export async function addLiftNote(exerciseId, body, date = localISO()) {
  const { error } = await sb.from('grit_exercise_notes').insert({ user_id: activeUser(), exercise_id: exerciseId, body: body.trim(), date });
  if (error) { console.warn('addLiftNote failed', error); return false; }
  return true;
}

export async function updateLiftNote(id, body) {
  const { error } = await sb.from('grit_exercise_notes').update({ body: body.trim(), updated_at: new Date().toISOString() }).eq('id', id);
  if (error) { console.warn('updateLiftNote failed', error); return false; }
  return true;
}

export async function deleteLiftNote(id) {
  const { error } = await sb.from('grit_exercise_notes').delete().eq('id', id);
  if (error) { console.warn('deleteLiftNote failed', error); return false; }
  return true;
}

// The note on a whole workout ("Today's note"); null clears it.
export async function updateSessionNote(sessionId, text) {
  const { error } = await sb.from('grit_sessions').update({ notes: text && text.trim() ? text.trim() : null }).eq('id', sessionId);
  if (error) { console.warn('updateSessionNote failed', error); return false; }
  return true;
}

// ── Weekly focus goal (one active at a time) ──────────────────────────────────
export async function loadActiveGoal(user = viewing()) {
  const { data } = await sb.from('grit_weekly_goals')
    .select('*').eq('user_id', user).eq('status', 'active').limit(1).maybeSingle();
  return data || null;
}

export async function loadGoalCheckins(goalId) {
  if (!goalId) return [];
  const { data } = await sb.from('grit_goal_checkins').select('*').eq('goal_id', goalId).order('date');
  return data || [];
}

export async function loadCompletedGoals(user = viewing()) {
  const { data } = await sb.from('grit_weekly_goals')
    .select('*').eq('user_id', user).neq('status', 'active')
    .order('created_at', { ascending: false });
  return data || [];
}

export async function createWeeklyGoal({ text, target_days, reward, week_start }) {
  const { data, error } = await sb.from('grit_weekly_goals')
    .insert({ user_id: activeUser(), text, target_days, reward: reward || null, week_start, status: 'active' })
    .select('*').single();
  if (error) { console.warn('createWeeklyGoal failed', error); return null; }
  return data;
}

export async function checkInGoal(goalId, dateISO) {
  const { error } = await sb.from('grit_goal_checkins')
    .insert({ goal_id: goalId, user_id: activeUser(), date: dateISO });
  if (error) { console.warn('checkInGoal failed', error); return false; }
  return true;
}

export async function completeGoal(goalId) {
  const { error } = await sb.from('grit_weekly_goals')
    .update({ status: 'completed', completed_at: new Date().toISOString() }).eq('id', goalId);
  if (error) { console.warn('completeGoal failed', error); return false; }
  return true;
}

export async function dropGoal(goalId) {
  const { error } = await sb.from('grit_weekly_goals').update({ status: 'dropped' }).eq('id', goalId);
  if (error) { console.warn('dropGoal failed', error); return false; }
  return true;
}
